import { useDebouncedSearch, useUsers, useOrganizations } from '@consigli/hooks';
import { StatusVariant } from '@consigli/types';
import { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { TbCrown } from 'react-icons/tb';

import { Badge, DataTable, Search, Combobox, Pagination, DEFAULT_PAGE_SIZE } from '@/components';

import { UserIdCell } from './user-id-cell';
import { UsersActionButton } from './users-action-button';

export const UsersTable = () => {
  const pageSize = DEFAULT_PAGE_SIZE;
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedSearch(searchTerm, 500);
  const [selectedOrgIds, setSelectedOrgIds] = useState<string[]>([]);

  const { users, paginatedCount, isFetching } = useUsers({
    page: page,
    pageSize: pageSize,
    search: debouncedSearchTerm,
    orgIds: selectedOrgIds.length > 0 ? selectedOrgIds : undefined,
  });

  const { organizations } = useOrganizations();

  const orgOptions = useMemo(() => {
    return organizations.map((org) => ({
      value: org.id,
      label: org.name,
    }));
  }, [organizations]);

  const columns: ColumnDef<(typeof users)[0], unknown>[] = [
    {
      header: 'Name',
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          {row.original.isSuperuser && (
            <TbCrown size={16} title="Superuser" className="shrink-0 text-blue-600" />
          )}
          <span>{`${row.original.firstName} ${row.original.lastName}`}</span>
        </div>
      ),
    },
    {
      header: 'Email',
      accessorKey: 'email',
    },
    {
      // Not accessorKey: this column renders no value, so an accessor would
      // compute one nothing reads. Headed for what it does, like Actions.
      id: 'copyId',
      header: 'Copy ID',
      cell: ({ row }) => <UserIdCell user={row.original} />,
    },
    {
      header: 'Organization',
      accessorKey: 'organizationName',
    },
    {
      header: 'Projects Access',
      accessorKey: 'projectAccess',
      cell: ({ row }) => row.original.projectAccess.length,
    },
    {
      header: 'Solution Viewer',
      accessorKey: 'solutionViewerAccess',
      cell: ({ row }) => {
        const hasAccess = row.original.solutionViewerAccess || row.original.isSuperuser;
        return (
          <Badge
            text={hasAccess ? 'Enabled' : 'Disabled'}
            variant={hasAccess ? StatusVariant.SUCCESS : StatusVariant.ERROR}
          />
        );
      },
    },
    {
      header: 'Datacenter',
      accessorKey: 'solutionViewerDatacenterAccess',
      cell: ({ row }) => {
        const hasAccess = row.original.solutionViewerDatacenterAccess || row.original.isSuperuser;
        return (
          <Badge
            text={hasAccess ? 'Enabled' : 'Disabled'}
            variant={hasAccess ? StatusVariant.SUCCESS : StatusVariant.ERROR}
          />
        );
      },
    },
    {
      header: 'Wastewater treatment',
      accessorKey: 'solutionViewerWaterTreatmentAccess',
      cell: ({ row }) => {
        const hasAccess =
          row.original.solutionViewerWaterTreatmentAccess || row.original.isSuperuser;
        return (
          <Badge
            text={hasAccess ? 'Enabled' : 'Disabled'}
            variant={hasAccess ? StatusVariant.SUCCESS : StatusVariant.ERROR}
          />
        );
      },
    },
    {
      header: 'Debug Mode',
      accessorKey: 'debugModeAccess',
      cell: ({ row }) => {
        const hasAccess = row.original.debugModeAccess || row.original.isSuperuser;
        return (
          <Badge
            text={hasAccess ? 'Enabled' : 'Disabled'}
            variant={hasAccess ? StatusVariant.SUCCESS : StatusVariant.ERROR}
          />
        );
      },
    },
    {
      header: 'Actions',
      accessorKey: 'actions',
      cell: ({ row }) => <UsersActionButton user={row.original} />,
    },
  ];

  function handlePageChange(newPage: number): void {
    setPage(newPage);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2">
        <Search
          className="w-[250px]"
          text={searchTerm}
          placeholder="Search for users"
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Combobox
          optionGroups={[
            {
              label: 'Organizations',
              options: orgOptions,
            },
          ]}
          className="w-[250px]"
          fallbackLabel="No organizations found"
          placeholder="Select Organization"
          value={selectedOrgIds}
          onValueChange={setSelectedOrgIds}
        />
      </div>
      <DataTable height="60vh" columns={columns} data={users} isLoading={isFetching} scrollable />
      <div className="relative flex items-center justify-center">
        <div className="absolute left-0 flex items-center gap-1.5">
          <TbCrown size={16} className="text-blue-600" />
          <span className="text-xs font-semibold text-gray-600">Superuser</span>
        </div>
        <Pagination
          onPageChange={handlePageChange}
          pageSize={pageSize}
          currentPage={page}
          totalCount={paginatedCount}
        />
      </div>
    </div>
  );
};
